<template>
  <AnalyticsLayout>
    <div class="dashboard-page">
      <!-- 整体看板 -->
      <div class="mb-8">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold text-gray-800">账号统计概览</h2>
          <div class="flex items-center space-x-3">
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              placeholder="选择月份"
              format="yyyy年MM月"
              value-format="yyyy-MM"
              @change="onMonthChange"
              class="w-40"
            />
            <el-button 
              type="primary" 
              icon="el-icon-refresh" 
              @click="refreshOverviewData"
              :loading="overviewLoading"
              size="small"
            >
              刷新
            </el-button>
          </div>
        </div>

        <!-- 统计卡片网格 -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
          <!-- 桥梁数量 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">关联桥梁数</p>
                <p class="text-2xl font-bold text-gray-900">{{ overviewData.bridgeCount || 0 }}</p>
              </div>
              <i class="el-icon-connection text-blue-500 text-2xl"></i>
            </div>
          </div>

          <!-- 累计通航总数 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">累计通航总数</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.totalNavigationCount) }}</p>
              </div>
              <i class="el-icon-ship text-green-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月通航总数 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-indigo-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月通航总数</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyNavigationCount) }}</p>
              </div>
              <i class="el-icon-date text-indigo-500 text-2xl"></i>
            </div>
          </div>

          <!-- 累计告警记录数 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">累计告警记录</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.totalAlertCount) }}</p>
              </div>
              <i class="el-icon-warning text-red-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月告警记录数 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月告警记录</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyAlertCount) }}</p>
              </div>
              <i class="el-icon-bell text-orange-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月偏航告警 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月偏航告警</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyDeviationCount) }}</p>
              </div>
              <i class="el-icon-position text-purple-500 text-2xl"></i>
            </div>
          </div>
        </div>

        <!-- 详细告警统计卡片 -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <!-- 当月超高告警 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-pink-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月超高告警</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyOverheightCount) }}</p>
              </div>
              <i class="el-icon-top text-pink-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月超速告警 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月超速告警</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyOverspeedCount) }}</p>
              </div>
              <i class="el-icon-odometer text-yellow-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月滞留告警 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-teal-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月滞留告警</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyDetentionCount) }}</p>
              </div>
              <i class="el-icon-time text-teal-500 text-2xl"></i>
            </div>
          </div>

          <!-- 当月黑名单告警 -->
          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-gray-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">当月黑名单告警</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatNumber(overviewData.monthlyBlacklistCount) }}</p>
              </div>
              <i class="el-icon-user-solid text-gray-500 text-2xl"></i>
            </div>
          </div>
        </div>
      </div>


      <!-- 桥梁具体分析 -->
      <div class="mb-8">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold text-gray-800">桥梁具体分析</h2>
          <div class="flex items-center space-x-3">
            <el-select
              v-model="selectedBridgeId"
              placeholder="请选择桥梁"
              filterable
              @change="onBridgeChange"
              class="w-48"
            >
              <el-option
                v-for="bridge in overviewData.bridgeList || []"
                :key="bridge.id"
                :label="bridge.bridgeName"
                :value="bridge.id"
              />
            </el-select>
            <el-button 
              type="primary" 
              icon="el-icon-refresh" 
              @click="refreshBridgeChartData"
              :loading="isAnyChartLoading"
              size="small"
              :disabled="!selectedBridgeId"
            >
              刷新
            </el-button>
          </div>
        </div>

        <!-- 流量看板 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800">今日船舶流量统计</h3>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ trafficUpdateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshTrafficData"
                  :loading="trafficLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="trafficChart" class="h-80"></div>
          </div>

          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800">今日船舶类型分布</h3>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ vesselTypeUpdateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshVesselTypeData"
                  :loading="vesselTypeLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="vesselTypeChart" class="h-80"></div>
          </div>
        </div>

        <!-- 预警行为看板 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800">今日船舶告警统计（按类型次数）</h3>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ alertStatsUpdateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshAlertStatsData"
                  :loading="alertStatsLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="alertStatsChart" class="h-80"></div>
          </div>

          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800">今日船舶告警统计（按类型比例）</h3>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ alertTypeStatsUpdateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshAlertTypeStatsData"
                  :loading="alertTypeStatsLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="alertTypeStatsChart" class="h-80"></div>
          </div>
        </div>
      </div>
    </div>
  </AnalyticsLayout>
</template>

<script>
import AnalyticsLayout from '@/components/AnalyticsLayout.vue'
import { getCurrentAccountStatistics, getBridgeDailyStatistics } from '@/api/account-statistics'
import { getTrafficStats, getVesselTypeStats, getAlertTypeStats } from '@/api/visualization'
import { getAllBridges } from '@/api/bridge'
import bridgeStateManager from '@/utils/bridgeStateManager'
import * as echarts from 'echarts'

export default {
  name: 'DataDashboard',
  components: {
    AnalyticsLayout
  },
  data() {
    return {
      // 用户信息
      username: localStorage.getItem('username') || '用户',
      
      // 月份选择
      selectedMonth: this.getCurrentMonth(),
      
      // 桥梁选择
      selectedBridgeId: bridgeStateManager.getCurrentBridgeId(),
      
      // 概览数据
      overviewData: {},
      overviewLoading: false,
      
      // 图表实例
      trafficChart: null,
      vesselTypeChart: null,
      alertStatsChart: null,
      alertTypeStatsChart: null,
      
      // 图表数据加载状态
      trafficLoading: false,
      vesselTypeLoading: false,
      alertStatsLoading: false,
      alertTypeStatsLoading: false,
      
      // 更新时间
      trafficUpdateTime: '',
      vesselTypeUpdateTime: '',
      alertStatsUpdateTime: '',
      alertTypeStatsUpdateTime: ''
    }
  },
  
  computed: {
    // 检查是否有任何图表正在加载
    isAnyChartLoading() {
      return this.trafficLoading || this.vesselTypeLoading || this.alertStatsLoading || this.alertTypeStatsLoading
    }
  },
  
  mounted() {
    this.loadOverviewData()
  },
  
  beforeDestroy() {
    // 销毁图表实例
    this.destroyCharts()
  },
  
  methods: {
    // 获取当前月份
    getCurrentMonth() {
      const now = new Date()
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    },
    
    // 格式化数字
    formatNumber(num) {
      if (num == null || num === undefined) return '0'
      return num.toLocaleString()
    },
    
    // 格式化时间
    formatTime() {
      const now = new Date()
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
    },
    
    // 月份变化处理
    onMonthChange() {
      this.loadOverviewData()
    },
    
    // 桥梁变化处理
    onBridgeChange() {
      // 保存桥梁ID到localStorage
      bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
      
      if (this.selectedBridgeId) {
        this.loadChartData()
      }
    },
    
    // 加载概览数据
    async loadOverviewData() {
      this.overviewLoading = true
      try {
        // 先获取统计数据
        const response = await getCurrentAccountStatistics(this.selectedMonth)
        if (response.code === 200) {
          this.overviewData = response.data
          
          // 再加载桥梁列表（这会覆盖统计数据中的bridgeList，使用新接口的倒序数据）
          await this.loadBridgeList()
          
          // 如果没有选中桥梁，默认选择第一个
          if (!this.selectedBridgeId && this.overviewData.bridgeList && this.overviewData.bridgeList.length > 0) {
            this.selectedBridgeId = this.overviewData.bridgeList[0].id
            bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
          }
          
          // 验证当前选中的桥梁是否在列表中
          if (this.overviewData.bridgeList && this.overviewData.bridgeList.length > 0) {
            const currentBridge = this.overviewData.bridgeList.find(bridge => bridge.id === this.selectedBridgeId)
            if (!currentBridge) {
              this.selectedBridgeId = this.overviewData.bridgeList[0].id
              bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
            }
          }
          
          this.$nextTick(() => {
            this.loadChartData()
          })
        } else {
          this.$message.error(response.message || '加载数据失败')
        }
      } catch (error) {
        console.error('加载概览数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.overviewLoading = false
      }
    },
    
    // 加载桥梁列表（统一数据源）
    async loadBridgeList() {
      try {
        const response = await getAllBridges()
        if (response.code === 200 && response.data) {
          // 倒序排列桥梁列表
          const bridgeList = (response.data || [])
            .reverse()
            .map(bridge => ({
              id: bridge.id,
              bridgeName: bridge.bridgeName || bridge.name || `桥梁${bridge.id}`,
              bridgeCode: bridge.bridgeCode,
              latitude: bridge.latitude,
              longitude: bridge.longitude
            }))
          
          // 设置到overviewData.bridgeList
          this.$set(this.overviewData, 'bridgeList', bridgeList)
          
          // 保存桥梁列表到localStorage
          bridgeStateManager.setBridgeList(bridgeList)
          
          console.log('【DataDashboard】加载桥梁列表成功:', bridgeList.length, '条记录')
        } else {
          console.error('【DataDashboard】加载桥梁列表失败:', response.message)
        }
      } catch (error) {
        console.error('【DataDashboard】加载桥梁列表异常:', error)
      }
    },
    
    // 刷新概览数据
    refreshOverviewData() {
      this.loadOverviewData()
    },
    
    // 查看桥梁详情
    viewBridgeDetails(bridge) {
      this.selectedBridgeId = bridge.id
      this.loadChartData()
      // 滚动到图表区域
      this.$nextTick(() => {
        const chartSection = this.$el.querySelector('.grid.grid-cols-1.lg\\:grid-cols-2')
        if (chartSection) {
          chartSection.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    
    // 加载图表数据
    loadChartData() {
      if (!this.selectedBridgeId) return
      
      this.refreshTrafficData()
      this.refreshVesselTypeData()
      this.refreshAlertStatsData()
      this.refreshAlertTypeStatsData()
    },
    
    // 刷新桥梁图表数据
    refreshBridgeChartData() {
      if (!this.selectedBridgeId) {
        this.$message.warning('请先选择桥梁')
        return
      }
      
      this.loadChartData()
    },
    
    // 刷新流量数据
    async refreshTrafficData() {
      if (!this.selectedBridgeId) return
      
      this.trafficLoading = true
      try {
        const response = await getTrafficStats(this.selectedBridgeId)
        if (response.code === 200) {
          this.renderTrafficChart(response.data)
          this.trafficUpdateTime = this.formatTime()
        } else {
          this.$message.error(response.message || '加载流量数据失败')
        }
      } catch (error) {
        console.error('加载流量数据失败:', error)
        this.$message.error('加载流量数据失败')
      } finally {
        this.trafficLoading = false
      }
    },
    
    // 刷新船舶类型数据
    async refreshVesselTypeData() {
      if (!this.selectedBridgeId) return
      
      this.vesselTypeLoading = true
      try {
        const response = await getVesselTypeStats(this.selectedBridgeId)
        if (response.code === 200) {
          this.renderVesselTypeChart(response.data)
          this.vesselTypeUpdateTime = this.formatTime()
        } else {
          this.$message.error(response.message || '加载船舶类型数据失败')
        }
      } catch (error) {
        console.error('加载船舶类型数据失败:', error)
        this.$message.error('加载船舶类型数据失败')
      } finally {
        this.vesselTypeLoading = false
      }
    },
    
    // 刷新告警统计数据
    async refreshAlertStatsData() {
      if (!this.selectedBridgeId) return
      
      this.alertStatsLoading = true
      try {
        const response = await getAlertTypeStats(this.selectedBridgeId)
        if (response.code === 200) {
          this.renderAlertStatsChart(response.data)
          this.alertStatsUpdateTime = this.formatTime()
        } else {
          this.$message.error(response.message || '加载告警统计数据失败')
        }
      } catch (error) {
        console.error('加载告警统计数据失败:', error)
        this.$message.error('加载告警统计数据失败')
      } finally {
        this.alertStatsLoading = false
      }
    },
    
    // 刷新告警类型统计数据
    async refreshAlertTypeStatsData() {
      if (!this.selectedBridgeId) return
      
      this.alertTypeStatsLoading = true
      try {
        const response = await getAlertTypeStats(this.selectedBridgeId)
        if (response.code === 200) {
          this.renderAlertTypeStatsChart(response.data)
          this.alertTypeStatsUpdateTime = this.formatTime()
        } else {
          this.$message.error(response.message || '加载告警类型统计数据失败')
        }
      } catch (error) {
        console.error('加载告警类型统计数据失败:', error)
        this.$message.error('加载告警类型统计数据失败')
      } finally {
        this.alertTypeStatsLoading = false
      }
    },
    
    // 渲染流量图表
    renderTrafficChart(data) {
      if (!this.$refs.trafficChart) return
      
      if (this.trafficChart) {
        this.trafficChart.dispose()
      }
      
      this.trafficChart = echarts.init(this.$refs.trafficChart)
      
      const option = {
        title: {
          text: '今日船舶流量统计',
          textStyle: {
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.timeLabel || `${item.hour}:00`),
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '通航次数',
            type: 'bar',
            data: data.map(item => item.count || 0),
            itemStyle: {
              color: '#3b82f6'
            }
          }
        ]
      }
      
      this.trafficChart.setOption(option)
    },
    
    // 渲染船舶类型图表
    renderVesselTypeChart(data) {
      if (!this.$refs.vesselTypeChart) return
      
      if (this.vesselTypeChart) {
        this.vesselTypeChart.dispose()
      }
      
      this.vesselTypeChart = echarts.init(this.$refs.vesselTypeChart)
      
      const option = {
        title: {
          text: '今日船舶类型分布',
          textStyle: {
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 10,
          data: data.map(item => item.vesselTypeName)
        },
        series: [
          {
            name: '船舶类型',
            type: 'pie',
            radius: '50%',
            center: ['60%', '50%'],
            data: data.map(item => ({
              value: item.count || 0,
              name: item.vesselTypeName
            })),
            label: {
              show: true,
              position: 'outside',
              formatter: '{b}\n{c} ({d}%)',
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold',
              lineHeight: 16
            },
            labelLine: {
              show: true,
              length: 15,
              length2: 10,
              smooth: false
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      
      this.vesselTypeChart.setOption(option)
    },
    
    // 渲染告警统计图表
    renderAlertStatsChart(data) {
      if (!this.$refs.alertStatsChart) return
      
      if (this.alertStatsChart) {
        this.alertStatsChart.dispose()
      }
      
      this.alertStatsChart = echarts.init(this.$refs.alertStatsChart)
      
      const option = {
        title: {
          text: '今日告警统计（按类型次数）',
          textStyle: {
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.alertTypeName),
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '告警次数',
            type: 'bar',
            data: data.map(item => item.count || 0),
            itemStyle: {
              color: '#ef4444'
            },
            label: {
              show: true,
              position: 'top',
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold'
            }
          }
        ]
      }
      
      this.alertStatsChart.setOption(option)
    },
    
    // 渲染告警类型统计图表
    renderAlertTypeStatsChart(data) {
      if (!this.$refs.alertTypeStatsChart) return
      
      if (this.alertTypeStatsChart) {
        this.alertTypeStatsChart.dispose()
      }
      
      this.alertTypeStatsChart = echarts.init(this.$refs.alertTypeStatsChart)
      
      const option = {
        title: {
          text: '今日告警统计（按类型比例）',
          textStyle: {
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 10,
          data: data.map(item => item.alertTypeName)
        },
        series: [
          {
            name: '告警类型',
            type: 'pie',
            radius: '50%',
            center: ['60%', '50%'],
            data: data.map(item => ({
              value: item.count || 0,
              name: item.alertTypeName
            })),
            label: {
              show: true,
              position: 'outside',
              formatter: '{b}\n{c} ({d}%)',
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold',
              lineHeight: 16
            },
            labelLine: {
              show: true,
              length: 15,
              length2: 10,
              smooth: false
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      
      this.alertTypeStatsChart.setOption(option)
    },
    
    // 销毁图表
    destroyCharts() {
      if (this.trafficChart) {
        this.trafficChart.dispose()
        this.trafficChart = null
      }
      if (this.vesselTypeChart) {
        this.vesselTypeChart.dispose()
        this.vesselTypeChart = null
      }
      if (this.alertStatsChart) {
        this.alertStatsChart.dispose()
        this.alertStatsChart = null
      }
      if (this.alertTypeStatsChart) {
        this.alertTypeStatsChart.dispose()
        this.alertTypeStatsChart = null
      }
    }
  }
}
</script>

<style scoped>
/* 数据看板页面样式 */
.dashboard-page {
  padding: 20px;
  background: #f5f5f5;
  min-height: calc(100vh - 140px); /* 减去header和footer的高度 */
}

/* 卡片悬停效果 */
.dashboard-page .bg-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* 统计卡片边框增强 */
.dashboard-page .border-l-4 {
  border-left-width: 4px !important;
}

/* 图表容器 */
.dashboard-page [ref*="Chart"] {
  border-radius: 8px;
}

/* 按钮样式优化 */
.dashboard-page .el-button--primary {
  transition: all 0.3s ease;
}

.dashboard-page .el-button--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* 表格样式 */
.dashboard-page .el-table th {
  background: #fafafa !important;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .dashboard-page {
    padding: 15px;
  }
}

@media (max-width: 768px) {
  .dashboard-page {
    padding: 10px;
  }
}
</style>
